import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { OrganizationSettingsFiscalYearDialogStore } from './organization-settings-fiscal-year-dialog.store';
import {
    AccountingTypeEnum,
    accountingTypeOptions,
} from '../../../../../../../../types/src/mado/enum/accounting-type.enum';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { DividerModule } from '@openng/optimus-ui/divider';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { fb, routeParams } from '../../../../../modules/shared/shared.global-service';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { FiscalYearObject } from '@libs/types/src/mado/object-type/organization/fiscal-year.object';
import { FieldErrorFactory } from '../../../../../modules/shared/service/field-error.service';
import { SessionService } from '../../../../../services/session.service';
import { combineLatest } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import config from '../../../../../config/config';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import { fiscalYearFrag, fiscalYearMutationFrag } from './organization-settings-fiscal-year-dialog.query';

@Component({
    selector: 'app-organization-settings-fiscal-year-dialog',
    templateUrl: './organization-settings-fiscal-year-dialog.component.html',
    providers: [OrganizationSettingsFiscalYearDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        DividerModule,
        SelectInputComponent,
    ],
})
export class OrganizationSettingsFiscalYearDialogComponent extends BaseDialogComponent implements OnInit {
    protected readonly store = inject(OrganizationSettingsFiscalYearDialogStore);
    private readonly session = inject(SessionService);


    protected readonly data = this.dialogConfig.data.fiscalYear || null;

    protected form = fb.group({
        id: fb.control<number | null>(null),
        dateFrom: fb.control<Date | null>(null, [Validators.required, this.dateOverlapValidator()]),
        dateTo: fb.control<Date | null>(null, [Validators.required, this.dateFromBeforeDateToValidator(), this.dateOverlapValidator()]),
        accountingType: fb.control<string>(AccountingTypeEnum.SIMPLE, Validators.required),
    });

    protected readonly prefix = 'organization-settings.mado.fiscalYear.';

    constructor() {
        super();
        this.subs.push(
            combineLatest([
                this.form.controls.dateFrom.valueChanges,
            ]).pipe(debounceTime(config.debounceTime))
                .subscribe((dateFrom) => {
                    if (dateFrom && this.form.value.dateTo) {
                        this.form.controls.dateTo.markAsTouched();
                        this.form.controls.dateTo.updateValueAndValidity();
                    }
                }),
        );
    }

    ngOnInit() {
        this.loadFiscalYears();
        this.patchForm(this.data);
    }

    override save() {
        if (isInvalid(this.form)) return;

        const data = {
            dateFrom: this.form.value.dateFrom!.toISOString(),
            dateTo: this.form.value.dateTo!.toISOString(),
            accountingType: this.form.value.accountingType || AccountingTypeEnum.SIMPLE,
        };

        if (this.form.value.id) {
            this.store.api.updateFiscalYear(
                {
                    id: this.form.value.id,
                    ...data,
                },
                fiscalYearMutationFrag,
            ).then(() => {
                contentGlobalSuccess.next('common.updated');
                this.dialogRef.close(true);
            });

        } else if (routeParams.organizationId()) {
            this.store.api.createFiscalYear(
                {
                    organizationId: Number(routeParams.organizationId()),
                    ...data,
                },
                fiscalYearMutationFrag,
            ).then(() => {
                contentGlobalSuccess.next('common.created');
                this.dialogRef.close(true);
            });
        }
    }

    private patchForm(item: FiscalYearObject | null) {
        if (item) {
            this.form.patchValue({
                id: item.id,
                dateFrom: new Date(item.dateFrom),
                dateTo: item.dateTo ? new Date(item.dateTo) : null,
                accountingType: item.accountingType,
            });
        }
    }

    private dateFromBeforeDateToValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value && this.form?.value?.dateFrom && control.value < this.form.value.dateFrom) {
                return FieldErrorFactory.invalidValidityPeriodError();
            }
            return null;
        };
    }

    private dateOverlapValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value && this.store.items()) {
                const conflictingFiscalYears = this.store.items().filter((fiscalYear: FiscalYearObject) => {
                    if (this.data?.id === fiscalYear.id) return false;
                    const dateFrom = new Date(fiscalYear.dateFrom);
                    const dateTo = fiscalYear.dateTo ? new Date(fiscalYear.dateTo) : new Date();
                    return new Date(control.value) >= dateFrom && new Date(control.value) <= dateTo;
                });
                if (conflictingFiscalYears.length) {
                    return FieldErrorFactory.datePeriodOverlapError(
                        conflictingFiscalYears[0].dateFrom,
                        conflictingFiscalYears[0].dateTo || new Date().toISOString(),
                        this.session,
                    );
                }
            }
            return null;
        };
    }

    private loadFiscalYears() {
        const organizationId = routeParams.organizationId();
        if (organizationId) {
            const filter = new FilterBuilder();
            filter.where('organizationId', organizationId);
            filter.sort('dateFrom', 'DESC');
            filter.page(1);
            filter.limit(999);

            this.store.api.getFiscalYears(filter.filter(), fiscalYearFrag).then();
        }
    }

    protected readonly accountingTypeOptions = accountingTypeOptions;
}
